import React from 'react';
import { cn } from '../../utils/cn';

export interface Facts {
  rent?: number | null;
  rentKind?: 'warm' | 'kalt';
  zip?: string | null;
  rooms?: number | null;
  area?: number | null;
}

const integerFormatter = new Intl.NumberFormat('de-DE');

function formatRent(rent: number, rentKind?: 'warm' | 'kalt'): string {
  const suffix = rentKind ? ` ${rentKind}` : '';
  return `${integerFormatter.format(rent)} €${suffix}`;
}

function formatRooms(rooms: number): string {
  const value = Number.isInteger(rooms)
    ? String(rooms)
    : rooms.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${value} Zi.`;
}

function formatArea(area: number): string {
  return `${integerFormatter.format(area)} m²`;
}

function buildFactItems(facts: Facts): string[] {
  const items: string[] = [];
  if (facts.rent != null) items.push(formatRent(facts.rent, facts.rentKind));
  if (facts.zip != null) items.push(facts.zip);
  if (facts.rooms != null) items.push(formatRooms(facts.rooms));
  if (facts.area != null) items.push(formatArea(facts.area));
  return items;
}

/** Plain-text version matching the push format, e.g. "1.200 € | 12345 | 3 Zi. | 85 m²". */
export function factLineText(f: Facts): string {
  return buildFactItems(f).join(' | ');
}

const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'text-sm',
  md: '',
  lg: 'text-lg',
};

export interface FactLineProps {
  facts: Facts;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Renders present facts only, each as a sibling <span>, separated by a thin
 * vertical rule — never "·" — matching the real push format
 * "1.200 € | 12345 | 3 Zi. | 85 m²".
 */
export const FactLine: React.FC<FactLineProps> = ({ facts, className, size = 'md' }) => {
  const items = buildFactItems(facts);
  if (items.length === 0) return null;

  return (
    <p className={cn('tabular font-medium text-ink', sizeClasses[size], className)}>
      {items.map((item, index) => (
        <span key={`${index}-${item}`} className={cn(index > 0 && 'border-l border-line-strong/60 pl-2 ml-2')}>
          {item}
        </span>
      ))}
    </p>
  );
};
